// Дебиторка из СВОЕЙ схемы (invoice + payment) — own-ветка раздела /receivables.
//
// ЕДИНИЦА СЧЁТА ДРУГАЯ, И ЭТО ГОВОРИТСЯ ВСЛУХ. Зеркало перечисляет ЗАПИСИ
// смарт-процесса 134 «Платежи»; здесь строка — НЕПОГАШЕННЫЙ ИСХОДЯЩИЙ СЧЁТ:
// сумма счёта минус платежи этого счёта С ДАТОЙ ОПЛАТЫ (payment.invoice_id).
// Платёж без даты — ожидание денег, долг он не гасит.
//
// ЧЕМ OWN-ВЕТКА ЛУЧШЕ ЗЕРКАЛЬНОЙ: корзины считает ядро SQL-агрегатом ПО ВСЕЙ
// выборке, а не по загруженной странице. Чем хуже: часть зеркальной дебиторки в
// свою схему не переносилась (на 30.07.2026 — 44 неоплаченные записи на
// 8,65 млн ₽, у которых счёт не прошёл перенос), и счета без платёжной записи 134
// зеркало не видело вовсе — обе оговорки экран печатает числами из ответа ядра.
//
// Экран ТОЛЬКО ЧИТАЮЩИЙ — как и зеркальная ветка.

import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useEntityTitle, useRefs } from '../api/hooks'
import type { OwnParams, OwnSort } from '../api/recvOwn'
import { РАЗМЕР_СТРАНИЦЫ, useRecvOwnList, useRecvOwnSummary } from '../api/recvOwn'
import { EntityLink } from '../components/EntityLink'
import { EntityPicker } from '../components/EntityPicker'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import type { EntityRef } from '../lib/entities'
import { fmtDate, fmtMoney, plural } from '../lib/format'
// Заглушки-контрагенты — ТОТ ЖЕ список, что у зеркальной ветки (lib/payments.ts):
// вторая версия списка однажды разошлась бы с первой, и «пробная» стала бы зависеть
// от источника.
import { PILOT_COMPANIES } from '../lib/payments'

const ГОРЯЧАЯ = 'overdue'   // корзина, которую подсвечиваем — как на зеркальном экране

interface Props {
  /** Переключатель источника — рисует родитель (Receivables), сюда приходит готовым. */
  switcher: React.ReactNode
}

export default function ReceivablesOwn({ switcher }: Props) {
  const [sp, setSp] = useSearchParams()
  const companyId = sp.get('company') || ''

  const [assigned, setAssigned] = useState('')
  const [bucket, setBucket] = useState('')
  const [sort, setSort] = useState<OwnSort>('due')
  const [start, setStart] = useState(0)

  const refs = useRefs()

  const companyTitle = useEntityTitle('company', companyId || undefined)
  const companyValue: EntityRef | null = companyId
    ? { kind: 'company', id: companyId, title: companyTitle.data || `#${companyId}` }
    : null

  const базовые = useMemo<OwnParams>(
    () => ({ company: companyId || undefined, assigned: assigned || undefined }),
    [companyId, assigned],
  )
  const сводкаQ = useRecvOwnSummary(базовые)
  const списокQ = useRecvOwnList({ ...базовые, bucket: bucket || undefined, sort, start })

  const сводка = сводкаQ.data
  const список = списокQ.data
  const scope = список?._scope ?? сводка?._scope

  const resetAnd = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  const changeCompany = (v: EntityRef | null) => {
    setStart(0)
    const next = new URLSearchParams(sp)
    if (v) next.set('company', v.id)
    else next.delete('company')
    setSp(next, { replace: true })
  }

  return (
    <>
      <div className="page-head">
        <h1>Дебиторка</h1>
        {switcher}
        {/* Единица счёта у источника ДРУГАЯ, и подпись обязана её называть: «счетов: 2486»
            рядом с зеркальными «платежей: N» — это не одна цифра из двух источников. */}
        <span className="muted">
          {список
            ? `непогашенных счетов: ${список.total}${scope?.limited ? ' (в вашей зоне)' : ''}`
            : ''}
        </span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="receivables-own">
          <div style={{ flex: 2, minWidth: 220 }}>
            <EntityPicker
              kind="company" id="ro-company" label="Компания"
              value={companyValue} onChange={changeCompany}
            />
          </div>

          <div className="f" style={{ minWidth: 190 }}>
            <label htmlFor="ro-resp">Ответственный</label>
            <select id="ro-resp" value={assigned}
                    onChange={(e) => resetAnd(setAssigned)(e.target.value)}
                    disabled={refs.users.length === 0}>
              <option value="">любой</option>
              {refs.users.map((u) => (
                <option key={u.id} value={u.id}>{u.active ? u.name : `${u.name} (уволен)`}</option>
              ))}
            </select>
          </div>

          <div className="f" style={{ minWidth: 160 }}>
            <label htmlFor="ro-bucket">Корзина</label>
            <select id="ro-bucket" value={bucket}
                    onChange={(e) => resetAnd(setBucket)(e.target.value)}>
              <option value="">любая</option>
              {(сводка?.buckets ?? []).map((b) => (
                <option key={b.bucket} value={b.bucket}>{b.label}</option>
              ))}
            </select>
          </div>

          <div className="f">
            <label htmlFor="ro-sort">Сортировка</label>
            <select id="ro-sort" value={sort}
                    onChange={(e) => resetAnd(setSort)(e.target.value as OwnSort)}>
              <option value="due">по сроку оплаты</option>
              <option value="amount">по остатку</option>
              <option value="issued">по дате счёта</option>
              <option value="updated">по изменению</option>
            </select>
          </div>
        </FilterBar>

        <div className="hint" style={{ marginTop: 10 }}>
          Источник — своя схема: строка здесь — непогашенный исходящий счёт, а не запись
          «Платежи» Bitrix. Остаток = сумма счёта минус платежи с датой оплаты; платёж без
          даты — ожидание денег, долг он не гасит.
        </div>
      </div>

      <QueryState isLoading={сводкаQ.isLoading || списокQ.isLoading}
                  error={сводкаQ.error || списокQ.error} scope={scope}>
        {сводка && список && (
        <>
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="page-head" style={{ marginBottom: 10 }}>
            <h2 style={{ margin: 0 }}>Корзины срочности</h2>
            <div className="spacer" />
            {/* У зеркальной ветки здесь «по показанным строкам из N» — own-ветке
                оговорка не нужна: корзины посчитало ядро по всей выборке. */}
            <span className="hint">по всей выборке, а не по странице — считает ядро</span>
          </div>

          <div className="tiles">
            {сводка.buckets.map((b) => (
              <div key={b.bucket}
                   className={`tile${b.bucket === ГОРЯЧАЯ && b.count ? ' hot' : ''}`}>
                <div className="t-label">{b.label}</div>
                <div className="t-sum">{fmtMoney(b.sum)}</div>
                <div className="t-label">{b.count} шт.</div>
              </div>
            ))}
            <div className="tile">
              <div className="t-label">Итого долг</div>
              <div className="t-sum">{fmtMoney(сводка.total.sum)}</div>
              <div className="t-label">{сводка.total.count} шт.</div>
            </div>
          </div>

          {/* Оговорки — числами из ответа ядра, а не словами «есть нюансы». */}
          <div className="hint" style={{ marginTop: 10 }}>
            {сводка.notes.unlinked.count > 0 && (
              `Без платёжной записи 134: ${сводка.notes.unlinked.count} `
              + `${plural(сводка.notes.unlinked.count, 'счёт', 'счёта', 'счетов')} на `
              + `${fmtMoney(сводка.notes.unlinked.sum)} — дебиторка Bitrix этих долгов не `
              + 'видела, в итог они включены.'
            )}
            {сводка.notes.covered_unmarked.count > 0 && (
              ` Погашены платежами, но без даты оплаты на счёте: `
              + `${сводка.notes.covered_unmarked.count} — в долг не входят.`
            )}
            {сводка.notes.expected_payments > 0 && (
              ` Ожидаемых платежей (без даты оплаты): ${сводка.notes.expected_payments} — `
              + 'долг они не гасят.'
            )}
            {сводка.notes.no_company.count > 0 && (
              ` Без контрагента: ${сводка.notes.no_company.count} на `
              + `${fmtMoney(сводка.notes.no_company.sum)} — дыры переноса, история как есть.`
            )}
            {сводка.notes.non_rub_invoices > 0 && (
              ` Не в рублях: ${сводка.notes.non_rub_invoices} — сложены в итог числом,`
              + ' как и на зеркальном экране.'
            )}
            {' Часть зеркальной дебиторки в свою схему не переносилась (счёт не прошёл'
             + ' перенос) — точные числа даёт сверка, зеркальный ответ смотрят'
             + ' переключателем источника.'}
          </div>
          {сводка.hidden_no_assigned && сводка.hidden_no_assigned.count > 0 && (
            <div className="hint" style={{ marginTop: 6 }}>
              Ваша зона видит счета по ответственному, а у{' '}
              {сводка.hidden_no_assigned.count}{' '}
              {plural(сводка.hidden_no_assigned.count, 'счёта', 'счетов', 'счетов')} на{' '}
              {fmtMoney(сводка.hidden_no_assigned.sum)} ответственный не заполнен — они не
              показаны никому, кроме ролей без границ.
            </div>
          )}
        </div>

        <div className="card">
          {список.rows.length === 0 ? (
            <div className="empty">
              <div className="empty-title">
                {scope?.limited
                  ? 'В вашей зоне таких счетов нет — выборка сужена правами, а не фильтрами'
                  : 'Ничего не найдено под эти фильтры'}
              </div>
            </div>
          ) : (
          <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th>Счёт</th>
                <th>Компания</th>
                <th>Сделка</th>
                <th>Выставлен</th>
                <th>Срок</th>
                <th>Просрочка</th>
                <th className="num">Сумма</th>
                <th className="num">Погашено</th>
                <th className="num">Остаток</th>
                <th>Ответственный</th>
              </tr>
            </thead>
            <tbody>
              {список.rows.map((r) => {
                const пробная = r.company_bitrix_id !== null
                  && PILOT_COMPANIES.includes(String(r.company_bitrix_id))
                return (
                  <tr key={r.id}>
                    <td data-label="Счёт">
                      {r.no ? `№ ${r.no}` : `#${r.id}`}
                      {!r.linked && (
                        <span className="badge" style={{ marginLeft: 6 }}
                              title="У счёта нет ни одной платёжной записи 134 — дебиторка Bitrix этот долг не видела">
                          без записи 134
                        </span>
                      )}
                      {пробная && (
                        <span className="badge lose" style={{ marginLeft: 6 }}
                              title="Контрагент-заглушка (обкатка, обучение) — тот же список, что у зеркальной ветки">
                          пробная
                        </span>
                      )}
                    </td>
                    <td data-label="Компания">
                      <EntityLink kind="company" id={r.company_bitrix_id}
                                  title={r.company_title} empty="— без контрагента" />
                    </td>
                    <td data-label="Сделка">
                      <EntityLink kind="deal" id={r.deal_bitrix_id} title={r.deal_title} />
                    </td>
                    <td data-label="Выставлен">{fmtDate(r.issued_at)}</td>
                    <td data-label="Срок">{r.due_at ? fmtDate(r.due_at) : 'без срока'}</td>
                    <td data-label="Просрочка">{r.overdue_days !== null && r.overdue_days > 0
                      ? `${r.overdue_days} дн.` : '—'}</td>
                    <td data-label="Сумма" className="num">{fmtMoney(r.amount, r.currency)}</td>
                    <td data-label="Погашено" className="num">{fmtMoney(r.paid_covered, r.currency)}</td>
                    <td data-label="Остаток" className="num"><b>{fmtMoney(r.remainder, r.currency)}</b></td>
                    <td data-label="Ответственный">{r.assigned_name || '—'}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </div>
          )}

          {список.total > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={start === 0 || списокQ.isFetching}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, список.total)} из {список.total}
              </span>
              <button disabled={список.next === null || списокQ.isFetching}
                      onClick={() => setStart(список.next ?? start)}>
                вперёд →
              </button>
            </div>
          )}
        </div>
        </>
        )}
      </QueryState>
    </>
  )
}
